import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';

import componentStyles from './pc-icon.css.lit';
import icons from './icons';

type IconSize = 'sm' | 'base' | 'lg' | 'xl';

/**
 * Icon component
 * @element pc-icon
 * @slot - This element has a default slot
 */
@customElement('pc-icon')
export class PCIcon extends OutlineElement {
  static styles: CSSResultArray = [componentStyles];

  /**
   * If this is excluded, it suggests that the icon serves a purely decorative purpose.
   * If the intention is for the icon to provide information, ensure that the label
   * attribute is included to be accessible by assistive devices.
   */
  @property({ attribute: 'label' })
  label: string | boolean = false;

  /** The name of the icon to draw. */
  @property()
  name?: string;

  /** The size of the icon to draw. */
  @property()
  size: IconSize = 'base';

  @property({ type: Boolean, attribute: 'is-fixed' })
  isFixed = false;

  render(): TemplateResult | null {
    const iconClasses = {
      'icon': true,
      [this.size]: true,
      'is-fixed': this.isFixed,
    };

    const hasLabel = typeof this.label === 'string' && this.label.length > 0;
    const iconSVG = icons[this.name as keyof typeof icons];

    if (!iconSVG) {
      return null;
    }

    return html`
      <i
        class=${classMap(iconClasses)}
        role=${ifDefined(hasLabel ? 'img' : undefined)}
        aria-label=${ifDefined(hasLabel ? this.label : undefined)}
      >
        ${unsafeSVG(iconSVG)}
      </i>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-icon': PCIcon;
  }
}
